import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import camelizeKeys from 'app/util/camelizeKeys';

const MESSAGE_DETAIL_TAG = 'MessageDetails';
const MAILBOX_TAG = 'Mailbox';

const mailApi = createApi({
  reducerPath: 'mailApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/client_api/mail' }),
  tagTypes: [MAILBOX_TAG, MESSAGE_DETAIL_TAG],
  endpoints: (builder) => ({
    // Payment Queries
    createPayment: builder.mutation({
      query: ({ messageId }) => ({
        url: `received_messages/${messageId}/payment`,
        method: 'POST',
      }),
      transformResponse: camelizeKeys,
      invalidatesTags: (_result, _error, { messageId }) => {
        return [MAILBOX_TAG, { type: MESSAGE_DETAIL_TAG, id: messageId }];
      },
    }),

    // Billed Recipient Query
    getBilledRecipients: builder.query({
      query: ({ messageId, page }) => ({
        url: `/sent_messages/${messageId}/billed_recipients?page=${page}`,
      }),
      transformResponse: camelizeKeys,
    }),

    // Mailbox Queries
    getDirectMessagesMailbox: builder.query({
      providesTags: [MAILBOX_TAG],
      query: (params) => {
        return {
          url: '/direct_messages_mailbox',
          params,
        };
      },
      transformResponse: camelizeKeys,
    }),

    getSystemMessagesMailbox: builder.query({
      providesTags: [MAILBOX_TAG],
      query: (params) => {
        return {
          url: '/system_messages_mailbox',
          params,
        };
      },
      transformResponse: camelizeKeys,
    }),

    getSentMessagesMailbox: builder.query({
      query: (params) => {
        return {
          url: '/sent_messages_mailbox',
          params,
        };
      },
      transformResponse: camelizeKeys,
    }),

    // Message Details Queries
    getSentMessageDetails: builder.query({
      query: ({ messageId }) => {
        return {
          url: `/sent_messages/${messageId}`,
        };
      },
      transformResponse: camelizeKeys,
      providesTags: (_result, _error, messageId) => [{ type: MESSAGE_DETAIL_TAG, id: messageId }],
    }),

    getReceivedMessageDetails: builder.query({
      query: ({ messageId }) => {
        return {
          url: `/received_messages/${messageId}`,
        };
      },
      transformResponse: camelizeKeys,
      providesTags: (_result, _error, messageId) => [{ type: MESSAGE_DETAIL_TAG, id: messageId }],
    }),

    updateReceivedMessage: builder.mutation({
      query: ({ messageId, markAsRead }) => ({
        url: `/received_messages/${messageId}`,
        method: 'PUT',
        body: {
          message: {
            id: messageId,
            mark_as_read: markAsRead,
          },
        },
      }),
      transformResponse: camelizeKeys,
      invalidatesTags: [MAILBOX_TAG],
    }),
  }),
});

export default mailApi;

export const {
  useCreatePaymentMutation,
  useGetBilledRecipientsQuery,
  useGetDirectMessagesMailboxQuery,
  useLazyGetReceivedMessageDetailsQuery,
  useLazyGetSentMessageDetailsQuery,
  useGetSentMessagesMailboxQuery,
  useGetSystemMessagesMailboxQuery,
  useUpdateReceivedMessageMutation,
} = mailApi;
